/**
 * Proof & Traceability Panel — Forensic-grade transparency surface.
 */
import { Badge } from "@/components/ui/badge";
import { Fingerprint, Clock, Camera, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { MaskedForecast } from "@/data/fie/types";

interface Props {
  forecast: MaskedForecast;
  locked?: boolean;
}

export function ProofPanel({ forecast: f, locked }: Props) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Proof & Traceability
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3 w-3 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">
            This panel shows the cryptographic proof that this forecast is deterministic and reproducible.
            Any change to inputs, assumptions, or attributions produces a different fingerprint.
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        {/* Reproducibility */}
        <div className="space-y-1">
          <p className="text-muted-foreground">Reproducibility</p>
          {f.proof.reproducible ? (
            <div className="flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-success" />
              <span className="font-medium text-success">Verified</span>
            </div>
          ) : f.integrityStatus === "mismatch" ? (
            <div className="flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
              <span className="font-medium text-destructive">Mismatch</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium text-muted-foreground">Pending</span>
            </div>
          )}
        </div>

        {/* Fingerprint */}
        {f.fingerprint && (
          <div className="space-y-1">
            <p className="text-muted-foreground">Fingerprint</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help">
                  <Fingerprint className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-mono font-medium">{f.fingerprint.hash}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm text-xs">
                <p className="font-semibold mb-1">DJB2a Full-State Hash</p>
                <p>{f.fingerprint.inputCount} jobs hashed</p>
                <p>Assumption digest: {f.fingerprint.assumptionDigest}</p>
                <p>Attribution digest: {f.fingerprint.attributionDigest}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Snapshot */}
        <div className="space-y-1">
          <p className="text-muted-foreground">Snapshot</p>
          <div className="flex items-center gap-1">
            <Camera className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono font-medium truncate">{f.proof.snapshotId}</span>
          </div>
          {locked && (
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-[9px] h-4">
              Immutable
            </Badge>
          )}
        </div>

        {/* Generated */}
        <div className="space-y-1">
          <p className="text-muted-foreground">Generated</p>
          <p className="font-medium">{new Date(f.generatedAt).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">
            Verified: {new Date(f.proof.verifiedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
